'use client';

import { useTranslations } from 'next-intl';
import { useGlobalUI } from 'contexts/GlobalUIContext';
import styles from './PhotonComparison.module.css';

/* Photon vs Other Brands comparison — single flat list pattern adapted
   from `components/womens-health/CnOComparison.tsx` (How we compare).
   No pillar groupings, no chips, no descriptions. Photon page tokens
   for typography so it matches the rest of the page. */

type Mark = 'check' | 'cross' | 'question';

type TFunction = (key: string) => string;

interface Feature {
  name: string;
  description: string;
  photon: Mark;
  other: Mark;
  /** Optional pill tag above the row title (e.g. "with Ring"). Visual
   *  treatment mirrors the "PRO EXCLUSIVE" tag on the C&O comparison. */
  tag?: string;
}

/* Photon-exclusive software / experience features. The three shared
   hardware rows (Dual-chip / 6 sessions / 1:1 ratio) were removed per
   the latest spec — those advantages live elsewhere on the page. */
const getFeatures = (t: TFunction): Feature[] => [
  {
    name: t('photon.photonComparison.row.personalisedProtocols'),
    description: t('photon.photonComparison.row.personalisedProtocolsDesc'),
    photon: 'check',
    other: 'cross',
  },
  {
    name: t('photon.photonComparison.row.dataRecommendations'),
    description: t('photon.photonComparison.row.dataRecommendationsDesc'),
    photon: 'check',
    other: 'cross',
    tag: t('photon.photonComparison.row.withRingTag'),
  },
  {
    name: t('photon.photonComparison.row.preWorkoutTiming'),
    description: t('photon.photonComparison.row.preWorkoutTimingDesc'),
    photon: 'check',
    other: 'cross',
    tag: t('photon.photonComparison.row.withRingTag'),
  },
  {
    name: t('photon.photonComparison.row.adherenceTracking'),
    description: t('photon.photonComparison.row.adherenceTrackingDesc'),
    photon: 'check',
    other: 'cross',
  },
  {
    name: t('photon.photonComparison.row.overuseGuardrails'),
    description: t('photon.photonComparison.row.overuseGuardrailsDesc'),
    photon: 'check',
    other: 'cross',
  },
  {
    name: t('photon.photonComparison.row.noCooldown'),
    description: t('photon.photonComparison.row.noCooldownDesc'),
    photon: 'check',
    other: 'cross',
  },
];

const Check = ({ t }: { t: TFunction }) => (
  <span className={styles.glyph} aria-label={t('photon.photonComparison.ariaLabel.yes')}>
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 12.5l4.5 4.5L19 7"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const Cross = ({ t }: { t: TFunction }) => (
  <span
    className={styles.glyph}
    aria-label={t('photon.photonComparison.ariaLabel.no')}
    data-muted="true"
  >
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M7 7l10 10M17 7L7 17"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  </span>
);

const Question = ({ t }: { t: TFunction }) => (
  <span
    className={styles.glyph}
    aria-label={t('photon.photonComparison.ariaLabel.unclear')}
    data-muted="true"
  >
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M9 9a3 3 0 1 1 4.5 2.6c-.9.5-1.5 1.1-1.5 2.4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="17.5" r="1.1" fill="currentColor" />
    </svg>
  </span>
);

const renderMark = (m: Mark, t: TFunction) =>
  m === 'check' ? (
    <Check t={t} />
  ) : m === 'cross' ? (
    <Cross t={t} />
  ) : (
    <Question t={t} />
  );

const PhotonComparison = ({
  className,
  htmlId,
}: {
  className?: string;
  htmlId?: string;
}) => {
  const t = useTranslations('photon');
  const features = getFeatures(t);
  const globalUI = useGlobalUI();
  const stickyTop = `${
    globalUI.headerVisible ? globalUI.headerHeight ?? 0 : 0
  }px`;

  return (
    <section
      className={className ? `${styles.section} ${className}` : styles.section}
      id={htmlId}
    >
      <div className={styles.inner}>
        <div className={styles.head}>
          <h2 className={styles.heading}>
            {t('photon.photonComparison.heading.howWeCompare')}
          </h2>
          <p className={styles.subhead}>
            {t('photon.photonComparison.text.compareSubhead')}
          </p>
        </div>

        <div className={styles.stickyHeader} style={{ top: stickyTop }}>
          <div className={styles.headerCell} />
          <div className={styles.headerCell} data-photon="true">
            {t('photon.photonComparison.heading.ultrahumanPhoton')}
          </div>
          <div className={styles.headerCell}>
            {t('photon.photonComparison.heading.otherBrands')}
          </div>
        </div>

        <table className={styles.desktopTable}>
          <tbody>
            {features.map((f, i) => (
              <tr key={i}>
                <td className="feature">
                  {f.tag && <span className="feature-tag">{f.tag}</span>}
                  <span className="feature-title">{f.name}</span>
                  <span className="feature-description">{f.description}</span>
                </td>
                <td className="value value--photon">{renderMark(f.photon, t)}</td>
                <td className="value">{renderMark(f.other, t)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className={styles.mobileStickyHeader} style={{ top: stickyTop }}>
          <div />
          <div className="photon">
            {t('photon.photonComparison.heading.photon')}
          </div>
          <div>{t('photon.photonComparison.heading.others')}</div>
        </div>

        <table className={styles.mobileTable}>
          <tbody>
            {features.map((f, i) => (
              <tr key={i}>
                <td className="feature">
                  {f.tag && <span className="feature-tag">{f.tag}</span>}
                  <span className="feature-title">{f.name}</span>
                  <span className="feature-description">{f.description}</span>
                </td>
                <td className="value value--photon">{renderMark(f.photon, t)}</td>
                <td className="value">{renderMark(f.other, t)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default PhotonComparison;
